import { BillingGroup } from "@clockwise/schema";

// libraries
import * as React from "react";
import {
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
} from "@material-ui/core";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";

import { greys, theme, colors } from "@styles/color.styles";

import {
  MemberPerson,
  SortType,
  SortDirection,
  BillingGroupRoleEnum,
} from "../billing-groups-members.type";
import { MemberRow } from "../member-row";

export interface MembersTableProps {
  persons: MemberPerson[];
  billingGroups: BillingGroup[];
  sendingInvite: string;
  sortType: SortType;
  sortDirection: SortDirection;
  previousPersonId: string | null;
  nextPersonId: string | null;
  sendInvite: (email: string) => void;
  onSort: (sortType: SortType) => void;
  onPaginationAction: (pageToken: string | null, fetchNext: boolean) => void;
  onAdminSelectChange: (
    userId: string,
    billingGroupId: string,
    role: BillingGroupRoleEnum,
    personId: string,
    errorMsg?: string,
  ) => void;
  onRemoveMember: (personIds: string[], billingGroupId: string, errorMsg?: string) => void;
  onAddMember: (personId: string[], billingGroupId: string) => void;
  onRequestToJoinMember: (personId: string, billingGroupId: string, email: string) => void;
  selectedMembers: MemberPerson[];
  onSelectOneMember: (person: MemberPerson) => void;
  onSelectAllMembers: () => void;
  isSelectAllChecked: boolean;
  isSelectDisabled: boolean;
}

const useStyles = makeStyles({
  table: {
    tableLayout: "fixed",
    minWidth: 550,
  },
  table__head: {
    background: greys.palePaleGrey,
    "& .MuiTableCell-head": {
      fontWeight: 700,
      fontSize: 14,
      color: greys.standard,
      "& > .MuiTableSortLabel-root.MuiTableSortLabel-active": {
        color: greys.standard,
      },
    },
  },
  row__selected: {
    width: "5%",
    "@media (min-width: 768px)": {
      width: "5%",
    },
  },
  row__name: {
    width: "30%",
    "@media (min-width: 768px) and (max-width: 1620px)": {
      width: "30%",
    },
    "@media (min-width: 1621px)": {
      width: "30%",
    },
  },
  row__signup: {
    width: "15%",
    "@media (min-width: 768px)": {
      width: "15%",
    },
  },
  row__subscription: {
    width: "20%",
    "@media (min-width: 768px)": {
      width: "20%",
    },
  },
  row__role: {
    width: "15%",
    "@media (min-width: 768px)": {
      width: "15%",
    },
  },
  // row__status: {
  //   width: "10%",
  //   "@media (min-width: 768px)": {
  //     width: "10%",
  //   },
  // },
  row__invited: {
    width: "15%",
    "@media (min-width: 768px) and (max-width: 1620px)": {
      width: "15%",
    },
    "@media (min-width: 1621px)": {
      width: "15%",
    },
  },
  empty: {
    color: theme.textSecondary,
    padding: "12px 0px",
  },
});

export const MembersTable = ({
  persons,
  billingGroups,
  sendInvite,
  sortType,
  sortDirection,
  previousPersonId,
  nextPersonId,
  sendingInvite,
  onSort,
  onPaginationAction,
  onAdminSelectChange,
  onRemoveMember,
  onAddMember,
  onRequestToJoinMember,
  selectedMembers,
  onSelectOneMember,
  onSelectAllMembers,
  isSelectAllChecked,
  isSelectDisabled,
}: MembersTableProps) => {
  const classes = useStyles();

  if (!persons.length) {
    return <div className={classes.empty}>No teammates found</div>;
  }

  /**
   * pageToken as first personId in the current page along with
   * fetchNext false fetches previous page, pageToken as last personId
   * in the current page along with fetchNext true fetches next page.
   */

  const isPersonSelected = (person: MemberPerson) => {
    return !!selectedMembers
      ? selectedMembers.some((member) => member.targetCalendarId === person.targetCalendarId)
      : false;
  };

  const isSelectInteterminate =
    isSelectAllChecked && selectedMembers.length !== persons.length && selectedMembers.length > 0;

  const handleBackButtonClick = () => {
    if (previousPersonId) {
      onPaginationAction(previousPersonId, false);
    }
  };

  const handleNextButtonClick = () => {
    if (nextPersonId) {
      onPaginationAction(nextPersonId, true);
    }
  };

  const maybeRenderPaginationActions = () => {
    if (!nextPersonId && !previousPersonId) {
      return null;
    }

    return (
      <TableFooter>
        <TableRow>
          <TableCell colSpan={5} align="right">
            <IconButton onClick={handleBackButtonClick} disabled={!previousPersonId}>
              <KeyboardArrowLeft />
            </IconButton>
            <IconButton onClick={handleNextButtonClick} disabled={!nextPersonId}>
              <KeyboardArrowRight />
            </IconButton>
          </TableCell>
        </TableRow>
      </TableFooter>
    );
  };

  const isSelectAllActive = isSelectAllChecked && selectedMembers.length > 0;

  return (
    <Table className={classes.table}>
      <TableHead className={classes.table__head}>
        <TableRow>
          {!isSelectDisabled && (
            <TableCell className={classes.row__selected}>
              <Checkbox
                size="small"
                style={{
                  color: isSelectInteterminate
                    ? greys.slate
                    : isSelectAllActive
                    ? colors.green.standard
                    : greys.slate,
                }}
                onChange={onSelectAllMembers}
                checked={isSelectAllActive}
                indeterminate={isSelectInteterminate}
                disabled={isSelectDisabled}
              />
            </TableCell>
          )}
          <TableCell className={classes.row__name}>
            <TableSortLabel
              active={sortType === SortType.FullName}
              direction={sortDirection === SortDirection.Ascending ? "asc" : "desc"}
              onClick={() => onSort(SortType.FullName)}
            >
              Name
            </TableSortLabel>
          </TableCell>
          <TableCell className={classes.row__signup}>Sign-up date</TableCell>
          <TableCell className={classes.row__subscription}>Plan</TableCell>
          <TableCell className={classes.row__role}>Role</TableCell>
          <TableCell className={classes.row__invited}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {persons.map((person) => (
          <MemberRow
            key={person.targetCalendarId}
            person={person}
            sendInvite={sendInvite}
            sendingInvite={sendingInvite}
            onAdminSelectChange={onAdminSelectChange}
            onRemoveMember={onRemoveMember}
            onAddMember={onAddMember}
            onRequestToJoinMember={onRequestToJoinMember}
            persons={persons}
            billingGroups={billingGroups}
            isChecked={isPersonSelected(person)}
            onSelect={onSelectOneMember}
            isSelectDisabled={isSelectDisabled}
          />
        ))}
      </TableBody>
      {maybeRenderPaginationActions()}
    </Table>
  );
};
